/**
 * Per-project model config stored as JSON on `Project.modelConfig`, resolved by
 * the coordinator each prompt: a saved pin for the use case wins, else the
 * replo-agent-model flag, else the platform default. `openRouterEnabled` is a
 * separate transport switch, on unless a config sets it to false — false is the
 * kill switch back to direct Anthropic (GLM degrades to Sonnet).
 */
import { z } from "zod";

// Named subagents (agents/<name>.md) that can carry their own model.
export const OPENCODE_SUBAGENT_NAMES = [
  "figma",
  "replicate",
  "edit-from-direction",
  "seo-researcher",
  "support",
  "write-business-md",
  "write-design-md",
] as const;

// Upstream brand behind each choice, for logos/grouping in the UI.
export const agentModelProviderSchema = z
  .enum([
    "anthropic",
    "google",
    "zai",
    "moonshot",
    "openai",
    "deepseek",
    "meta",
    "qwen",
    "xai",
  ])
  .describe("AgentModelProvider");

// NOTE (Kevin, 2026-07-25): Choices use OpenRouter when enabled and their direct-provider route otherwise.
export const agentModelChoiceSchema = z
  .enum([
    "sonnet-5",
    "sonnet-5.5",
    "haiku-4-5",
    "opus-5",
    "opus-5-5",
    "opus-4-8",
    "gemini-3.6-flash",
    "gemini-3.7-flash",
    "gemini-3.8-flash",
    "glm-4.6",
    "glm-5.2",
    "glm-5.3",
    "kimi-3",
    "gpt-5.6-luna",
    "gpt-5.6-terra",
    "gpt-5.6-sol",
    "deepseek-v4-flash",
    "deepseek-v4-pro",
    "deepseek-v4.1-flash",
    "muse-spark-1.3",
    "qwen3.8-max",
    "grok-4.6",
  ])
  .describe("AgentModelChoice");

// Everything that independently selects a model: session types + "agent:" subagents.
// (Completeness vs OpencodeSessionType/OPENCODE_SUBAGENT_NAMES is enforced at the coordinator call sites.)
export const agentModelUseCaseSchema = z
  .enum([
    "main",
    "subagent",
    "selectModeInlineEdit",
    "publish",
    "gitConflict",
    "duplicatePage",
    "deletePage",
    "analyticsScriptDetection",
    "filesystemSession",
    "scheduledTask",
    "memoryCuration",
    "tutorialPersonalization",
    "starterSuggestions",
    "proactiveFindings",
    "siteReport",
    "agent:figma",
    "agent:replicate",
    // The subagent is gone; the value stays so project configs saved with it still parse.
    "agent:build-from-direction",
    "agent:edit-from-direction",
    "agent:seo-researcher",
    "agent:support",
    "agent:write-business-md",
    "agent:write-design-md",
  ])
  .describe("AgentModelUseCase");

// Reasoning effort for every agent the config selects a model for. Absent
// keeps each model's baked options, so existing configs are unchanged. The
// values are opencode variant ids: opencode spells each per provider
// (Anthropic effort, OpenRouter reasoning.effort, Google thinkingLevel) and
// ignores one a model does not offer, so `max` reaches Anthropic and `xhigh`
// reaches OpenAI-family models without a config failing anywhere else.
export const agentModelEffortSchema = z
  .enum(["low", "medium", "high", "xhigh", "max"])
  .describe("AgentModelEffort");

export const agentModelConfigSchema = z
  .object({
    // Partial so configs predating a new use case still parse; resolver fills gaps per-key.
    models: z
      .partialRecord(agentModelUseCaseSchema, agentModelChoiceSchema)
      .optional(),
    // Transport switch: absent/true = OpenRouter (default); false is the kill switch back to direct Anthropic.
    openRouterEnabled: z.boolean().optional(),
    effort: agentModelEffortSchema.optional(),
    // Mid-turn model fallback when a provider fails: absent/true = on. Evals set false so a turn never silently runs a different model than the arm.
    fallbackEnabled: z.boolean().optional(),
  })
  .describe("AgentModelConfig");

export function getDefaultAgentModelConfig(): z.infer<
  typeof agentModelConfigSchema
> {
  return {};
}

// NOTE (Gabe, 2026-09-23): the persisted shape keeps `useDefaultModels: false`
// because the previous schema requires the key and its reader returns null
// (platform defaults) for a row that fails to parse, so a rollback would
// otherwise drop every pin saved after this shipped. False is the mode whose
// old resolution matches the current one (pin → flag → default), and the
// current schema strips the key on read.
// TODO (Gabe, 2026-09-23, REPL-32578): remove once the rollback window is
// closed on purpose; the --sweep-dead-key step runs only after that removal
// has deployed.
export function toPersistedAgentModelConfig(
  config: z.infer<typeof agentModelConfigSchema>,
): Record<string, unknown> {
  return { useDefaultModels: false, ...agentModelConfigSchema.parse(config) };
}
